import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SCHOOLS, type SchoolId } from '@crossmatch/shared';

import { newsApi, type ParseNewsPayload } from '../api/news.js';
import { toUserMessage } from '../api/errors.js';
import { Card } from '../components/Card.js';
import { Button } from '../components/Button.js';
import { Tag } from '../components/Tag.js';
import { Loading } from '../components/Loading.js';
import { EmptyState } from '../components/EmptyState.js';
import { ErrorState } from '../components/ErrorState.js';
import { schoolFullLabel } from '../lib/format.js';
import { useSession } from '../store/session.js';
import { toast } from '../store/toast.js';
import type { NewsDto, NewsParsedDto } from '../api/types.js';

const RAW_MAX = 5000;

/** AI 解析草稿卡片：展示解析结果 + 可编辑确认表单（确认后才建赛事）。 */
function DraftCard({ news, parsed, onConfirmed }: { news: NewsDto; parsed: NewsParsedDto; onConfirmed: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(parsed.title ?? '');
  const [season, setSeason] = useState('');
  const [scope, setScope] = useState<'common' | 'exclusive'>(parsed.scope);
  const [schools, setSchools] = useState<SchoolId[]>((parsed.schools ?? []) as SchoolId[]);

  const confirm = useMutation({
    mutationFn: () =>
      newsApi.confirm(news.id, {
        name,
        season,
        scope,
        schools: schools.map((s) => String(s)),
      }),
    onSuccess: (data) => {
      toast.success('已创建赛事', `赛事码 ${data.event.code}`);
      void queryClient.invalidateQueries({ queryKey: ['news'] });
      onConfirmed();
    },
    onError: (error) => toast.error(`创建失败：${toUserMessage(error)}`),
  });

  const toggleSchool = (id: SchoolId) =>
    setSchools((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));

  const valid = name.trim().length > 0;

  return (
    <Card
      title="AI 解析草稿"
      subtitle="请核对以下识别结果，确认无误后再创建赛事"
      actions={<Tag tone="brand">{parsed.via === 'llm' ? 'AI 智能' : '规则识别'}</Tag>}
    >
      <div className="stack">
        <label className="field">
          <span className="field-label">赛事名称</span>
          <input className="input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
        </label>
        <div className="row-tight">
          <label className="field grow">
            <span className="field-label">赛季</span>
            <input className="input" value={season} maxLength={40} placeholder="如 2026春" onChange={(e) => setSeason(e.target.value)} />
          </label>
          <label className="field">
            <span className="field-label">归属维度</span>
            <select className="input" value={scope} onChange={(e) => setScope(e.target.value as 'common' | 'exclusive')}>
              <option value="common">跨校共性</option>
              <option value="exclusive">某校专属</option>
            </select>
          </label>
        </div>

        <div className="field">
          <span className="field-label">参与学校（可多选）</span>
          <div className="chip-grid">
            {SCHOOLS.map((info) => (
              <button
                key={info.id}
                type="button"
                className={schools.includes(info.id) ? 'chip chip--on' : 'chip'}
                onClick={() => toggleSchool(info.id)}
              >
                {info.name}
              </button>
            ))}
          </div>
        </div>

        <div className="news-parsed">
          <div className="news-parsed-row">
            <span className="muted small">识别时间</span>
            <span>{parsed.eventTime ?? '未识别'}</span>
          </div>
          <div className="news-parsed-row">
            <span className="muted small">技能关键词</span>
            <span>{parsed.skills.length > 0 ? parsed.skills.join('、') : '未识别'}</span>
          </div>
          <div className="news-parsed-row">
            <span className="muted small">摘要</span>
            <span>{parsed.summary || '—'}</span>
          </div>
        </div>

        <div className="row">
          <Button variant="primary" disabled={!valid} loading={confirm.isPending} onClick={() => confirm.mutate()}>
            确认并创建赛事
          </Button>
        </div>
        <p className="field-hint">确认后将以「开放中」状态创建赛事；草稿保留在下方资讯库，可随时回溯。</p>
      </div>
    </Card>
  );
}

/** 资讯库条目卡片（已确认的高亮关联赛事）。 */
function NewsItem({ news }: { news: NewsDto }) {
  const s = news.scope === 'exclusive' ? '某校专属' : '跨校共性';
  return (
    <div className="card news-item">
      <div className="row-tight">
        <strong className="news-item-title">{news.title ?? news.parsed.title ?? '（未命名资讯）'}</strong>
        <Tag tone={news.status === 'confirmed' ? 'ok' : 'muted'}>{news.status === 'confirmed' ? '已落库' : '草稿'}</Tag>
        <Tag tone="brand">{s}</Tag>
      </div>
      <div className="muted small news-item-summary">{news.parsed.summary || news.rawText.slice(0, 80)}</div>
      <div className="muted small">
        {news.schools.length > 0 ? `参与学校：${news.schools.map((x) => schoolFullLabel(x as never)).join('、')} · ` : ''}
        解析方式：{news.parsed.via === 'llm' ? 'AI' : '规则'}
        {news.linkedEventCode ? ` · 已建赛事 ${news.linkedEventCode}` : ''}
      </div>
    </div>
  );
}

export function NewsPage() {
  const user = useSession((state) => state.user);
  const [rawText, setRawText] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [draft, setDraft] = useState<{ news: NewsDto; parsed: NewsParsedDto } | null>(null);
  const queryClient = useQueryClient();

  const isOrganizer = user?.role === 'organizer' || user?.role === 'admin';

  const listQ = useQuery({ queryKey: ['news'], queryFn: () => newsApi.list(), enabled: isOrganizer });
  const parse = useMutation({
    mutationFn: (payload: ParseNewsPayload) => newsApi.parse(payload),
    onSuccess: (data) => {
      setDraft(data);
      void queryClient.invalidateQueries({ queryKey: ['news'] });
      toast.success('AI 解析完成', data.parsed.via === 'llm' ? '已用大模型智能识别' : '当前为规则识别（未配置大模型）');
    },
    onError: (error) => toast.error(`解析失败：${toUserMessage(error)}`),
  });

  if (!isOrganizer) {
    return (
      <div className="stack">
        <div className="page-head">
          <h1 className="page-title">竞赛资讯</h1>
        </div>
        <EmptyState
          icon="◎"
          title="资讯工作台仅对赛事组织方开放"
          description="这里用于粘贴赛事原文、AI 识别并创建赛事。学生查看赛事与倒计时，请到「赛事联动」。"
          action={
            <Link className="btn btn-primary" to="/events">
              去赛事联动
            </Link>
          }
        />
      </div>
    );
  }

  const charCount = rawText.length;

  return (
    <div className="stack">
      <div className="page-head">
        <h1 className="page-title">竞赛资讯</h1>
        <p className="page-sub">
          粘贴赛事通知 / 公告原文，AI 自动抽取赛事名称、时间、归属学校与技能关键词，确认后一键创建分类赛事。
        </p>
      </div>

      <Card title="AI 智能读取" subtitle="粘贴原文，自动归类到「跨校共性 / 某校专属」">
        <div className="stack">
          <label className="field">
            <span className="field-label">竞赛原文</span>
            <textarea
              className="input textarea"
              rows={6}
              maxLength={RAW_MAX}
              value={rawText}
              placeholder="例如：东华大学承办的 2026 松江大学生创新创业大赛，报名截止 2026-03-15，欢迎有编程、设计的同学组队…"
              onChange={(e) => setRawText(e.target.value)}
            />
            <span className="field-hint">{charCount}/{RAW_MAX}</span>
          </label>
          <label className="field">
            <span className="field-label">来源链接（可选）</span>
            <input
              className="input"
              value={sourceUrl}
              placeholder="https://…"
              onChange={(e) => setSourceUrl(e.target.value)}
            />
          </label>
          <div className="row">
            <Button
              variant="primary"
              disabled={rawText.trim().length === 0}
              loading={parse.isPending}
              onClick={() => parse.mutate({ rawText, sourceUrl: sourceUrl.trim() || null })}
            >
              AI 智能解析
            </Button>
          </div>
        </div>
      </Card>

      {draft ? (
        <DraftCard
          news={draft.news}
          parsed={draft.parsed}
          onConfirmed={() => {
            setDraft(null);
            setRawText('');
            setSourceUrl('');
          }}
        />
      ) : null}

      <Card title="资讯库" subtitle="所有解析草稿与已创建赛事的留痕">
        {listQ.isPending ? (
          <Loading label="读取资讯库…" rows={2} />
        ) : listQ.isError ? (
          <ErrorState error={listQ.error} title="读取失败" onRetry={() => void listQ.refetch()} />
        ) : listQ.data.news.length === 0 ? (
          <EmptyState icon="❏" title="还没有资讯" description="在上方粘贴并解析第一条竞赛原文吧。" />
        ) : (
          <div className="stack">
            {listQ.data.news.map((news) => (
              <NewsItem key={news.id} news={news} />
            ))}
          </div>
        )}
      </Card>

      {draft?.news.linkedEventCode ? (
        <p className="muted small">
          已创建赛事，<Link to={`/events/${draft.news.linkedEventCode}`}>前往赛事详情</Link>。
        </p>
      ) : null}
    </div>
  );
}
